"use client";

import { FC, forwardRef, useEffect, useImperativeHandle } from "react";
import { useMediaQuery } from "usehooks-ts";
import type {
  CalendarPostPopoverState,
  PinnedAnchorRect,
} from "../../hooks/useCalendarPostPopover";
import type { PublicationModel } from "../../types/models";
import Dialog from "../Dialog";
import PopoverPannel from "../PopoverPannel";
import CalendarPostPopoverContent, {
  type CalendarPostActions,
} from "./CalendarPostPopoverContent";

/**
 * The stand-in the panel is pinned to once the user pages. Same house shape as
 * PopoverPannel's `ensureViewportBoundaryElement`, for the same reason:
 * react-tiny-popover only understands boxes.
 *
 * `position: fixed` IS the mechanism. A fixed element's
 * `getBoundingClientRect()` does not change when anything scrolls, so the
 * library's per-frame rect comparison finds nothing to do and never
 * repositions. It lives on `document.body` so no transformed or filtered
 * ancestor in either host can turn it into a containing-block-relative box, and
 * it is `visibility:hidden; pointer-events:none` so it is a rect and nothing
 * else — never painted, never hit-tested, never focusable.
 *
 * A singleton is correct: only one inspector is open in the app at a time, and
 * the two hosts never coexist.
 */
let pinnedAnchorElement: HTMLDivElement | null = null;

const pinnedAnchorAt = (rect: PinnedAnchorRect): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  if (!pinnedAnchorElement || !document.body.contains(pinnedAnchorElement)) {
    const el = document.createElement("div");
    el.setAttribute("data-calendar-inspector-anchor", "");
    el.setAttribute("aria-hidden", "true");
    el.style.cssText =
      "position:fixed;pointer-events:none;visibility:hidden;z-index:-1;";
    document.body.appendChild(el);
    pinnedAnchorElement = el;
  }
  pinnedAnchorElement.style.left = `${rect.left}px`;
  pinnedAnchorElement.style.top = `${rect.top}px`;
  pinnedAnchorElement.style.width = `${rect.width}px`;
  pinnedAnchorElement.style.height = `${rect.height}px`;
  return pinnedAnchorElement;
};

// react-tiny-popover positions against the ref of its one child. This proxy
// renders nothing and hands an element through the ref, so the popover gets
// auto placement, viewport boundary and repositioning with no ghost box.
//
// But the anchor is not where the panel STARTS, it is what the panel FOLLOWS:
// the library re-reads the anchor's viewport rect on every animation frame and
// repositions on any change, so while a tile is the anchor the panel rides that
// tile as the grid scrolls. So the tile is the anchor only until the first
// navigation step; from then on it is the frozen stand-in above, which is what
// lets the calendar scroll — and change month, which destroys the tile
// outright — underneath a panel that does not move. The stand-in is handed the
// tile's exact rect, so the swap itself repositions nothing.
//
// `reposition={false}` is NOT an alternative: it only drops the position-list
// fallback and the boundary nudge, so the panel would follow the tile straight
// off the edge of the screen.
const AnchorProxy = forwardRef<
  HTMLElement | null,
  { element: HTMLElement | null; pinnedRect: PinnedAnchorRect | null }
>(({ element, pinnedRect }, ref) => {
  useImperativeHandle(
    ref,
    () => (pinnedRect ? pinnedAnchorAt(pinnedRect) : element) as HTMLElement,
    [element, pinnedRect],
  );
  return null;
});
AnchorProxy.displayName = "CalendarPostPopoverAnchor";

/**
 * The post popover — anchored to the clicked tile on desktop, the
 * bottom-docked Dialog on narrow screens — holding the same content in both:
 * the short playing under its caption and status, with a stable action strip
 * at the top. Outside click and Escape close
 * it; the calendar stays visible, undimmed.
 *
 * Shared by BOTH calendars — the global `/calendar` page and the Schedule
 * workspace's Calendar tab. Everything that differs between them arrives as
 * an injected action (`CalendarPostActions`) or as
 * `resolvePersistedPublication`; nothing here branches on which host it is in.
 */
const CalendarPostPopover: FC<
  {
    state: CalendarPostPopoverState | null;
    /** Live rows for the inspected post and its same-short navigation. */
    publications: PublicationModel[] | undefined;
    now: Date;
    onClose: () => void;
    /** The hook's `inspect`, straight through. This shell — the only thing
     *  that knows whether this is the sheet or the anchored panel — fills in
     *  the options; neither host has to. */
    onInspect: (
      publication: PublicationModel,
      options: { reveal: boolean },
    ) => void;
    /** Passed straight through to the content — see its own doc. Left in the
     *  rest below so this shell never has to know what it means. */
    resolvePersistedPublication?: (
      publication: PublicationModel,
    ) => PublicationModel | null;
    /** The scheduling session's existing saved-post icon rule. */
    showSavedScheduledStatus?: (publication: PublicationModel) => boolean;
  } & CalendarPostActions
> = ({ state, publications, now, onClose, onInspect, ...actions }) => {
  const isNarrow = useMediaQuery("(max-width: 767px)");
  const isOpen = state !== null;

  // Escape at the document, bubble phase: a nested menu's own Escape stops
  // propagation first, so the popover only closes once nothing sits on
  // top of it.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Close if the inspected row disappears, but allow paging across periods.
  useEffect(() => {
    if (state && publications &&
      !publications.some((post) => post.id === state.inspectedPublicationId)) {
      onClose();
    }
  }, [state, publications, onClose]);

  // Keep the controls mounted across videos so repeated clicks and keyboard
  // focus stay in place. CalendarPostPlayer resets playback on video changes.
  const content = state ? (
    <CalendarPostPopoverContent
      publication={state.publication}
      inspectedPublicationId={state.inspectedPublicationId}
      publications={publications}
      now={now}
      compact={isNarrow}
      onInspectPublication={(publication) =>
        onInspect(publication, { reveal: !isNarrow })
      }
      {...actions}
    />
  ) : null;

  if (isNarrow) {
    return (
      // The inspector owns its media, notice and action-bar surfaces.
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-md !bg-transparent"
        disabledPadding
      >
        {/* Keep the sheet's close button clear of the media action strip. */}
        <div className="pt-14">{content}</div>
      </Dialog>
    );
  }

  // Gated on a CONNECTED anchor, not merely a non-null one. A detached tile —
  // the scheduler remounting its Calendar on its `key`, or the Calendar tab
  // unmounting under an open inspector — measures as an all-zero DOMRect,
  // which is truthy enough for the library to reposition against, and the panel
  // would be nudged into the viewport's top-left corner. Once pinned, the
  // tile's fate stops mattering at all, which is what makes a cross-period step
  // safe.
  const isAnchored =
    Boolean(state?.pinnedRect) || Boolean(state?.element?.isConnected);

  return (
    <PopoverPannel
      isOpen={isOpen && isAnchored}
      positions={["right", "left", "bottom", "top"]}
      // Centred on the tile rather than hung from its top edge: the player is
      // far taller than a tile, so a `start` align made it read as a panel
      // dropping AWAY from the post. `reposition` still pulls it back inside
      // the viewport when a tile sits near the top or bottom of the grid.
      align="center"
      padding={8}
      reposition
      // The content owns its surfaces, so the shell's
      // shared fill, border and min-width all come off (`!` because the
      // base sets `bg-white` and `dark:bg-surface`, and a bare
      // `bg-transparent` would only beat the light one). Nothing sizes the
      // box here either — the content is a fixed-ratio player that caps
      // itself against the viewport, and react-tiny-popover measures it.
      // Note it also writes `overflow: visible` into this element's style
      // ATTRIBUTE (POPOVER_STYLE), which no class can outrank, so the
      // content's corners are clipped by its own `overflow-hidden`,
      // never by a radius here.
      containerClassName="!bg-transparent dark:!bg-transparent !border-0 !min-w-0 rounded-xl"
      onClickOutside={(event) => {
        // A click in a portal spawned from our content (the `…` menu) or in
        // a stacked dialog is ours; anything else closes.
        //
        // The anchor tile counts as ours too. react-tiny-popover exempts
        // clicks inside its own anchor, and swapping that anchor for the
        // pinned stand-in takes the exemption away — this restores exactly
        // that and no more. Deliberately NOT widened to every
        // `[data-publication-id]`: a click on any OTHER tile is unchanged from
        // today (it counts as outside, closes, and that tile's own click
        // re-opens), and widening it would stop disabled and out-of-scope
        // tiles dismissing the popover, which they do now.
        const path = typeof event.composedPath === "function" ? event.composedPath() : [];
        const isInsideOwnLayer = path.some(
          (node) =>
            node === state?.element ||
            (node instanceof HTMLElement &&
              (node.classList.contains("popover-menu") ||
                node.classList.contains("ReactModal__Overlay"))),
        );
        if (isInsideOwnLayer) return;
        onClose();
      }}
      content={content ?? <></>}
    >
      <AnchorProxy
        element={state?.element ?? null}
        pinnedRect={state?.pinnedRect ?? null}
      />
    </PopoverPannel>
  );
};

export default CalendarPostPopover;
